<template>
  <div :class="{ invalid }">
    <label class="block mb-1" :class="{ 'p-error': invalid, 'p-disabled': disabled }" v-if="!hideLabel">{{
      label
    }}</label>
    <div class="field-wrapper">
    <VueTinymce
      ref="inputEl"
      :class="{ 'p-invalid': invalid }"
      v-model="value"
      :disabled="disabled"
      script-src="/scripts/tinymce/tinymce.min.js"
      :setting="{
        ...editorSettings,
        skin,
      }"
      @init="init"
      :key="refreshKey"
    />
      <small v-if="allowTranslations" class="field-icon-right" v-tooltip.left="`${$t('Translations')}`">
        <a href="#" @click.stop.prevent="openTranslations()">
          <i class="fa fa-globe" aria-hidden="true"></i>
        </a>
      </small>
    </div>
    <template v-if="error && showErrors">
      <small v-if="typeof error === 'string'" class="p-error">{{ error }}</small>
      <template v-else-if="typeof error === 'object' && error.length">
        <template v-for="errorObj in error">
          <small class="p-error">{{ errorObj.$message }}</small>
        </template>
      </template>
    </template>
  </div>

  <TranslationsPopup
    v-if="showTranslations"
    :field="field"
    @close="closeTranslations"
    @save="onTranslationsSave"
  />
</template>

<script>
import VueTinymce from '@jsdawn/vue3-tinymce';
import { mapGetters } from 'vuex';
import TranslationsPopup from "@/components/popups/TranslationsPopup.vue";
import {objectVariable} from "@/js/document/document";

export default {
  name: 'EditorInput',
  components: {TranslationsPopup, VueTinymce },
  props: {
    modelValue: String,
    label: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    error: {
      default: null,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    showErrors: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    overrideSettings: {
      type: Object,
      default: null,
    },
    appendSettings: {
      type: Object,
      default: null,
    },
    avoidSanitization: {
      type: Boolean,
      default: false,
    },
    allowTranslations: {
      type: Boolean,
      default: false
    },
    translationsField: {
      type: String,
      default: null
    },
    translations: {
      type: Object,
      default: null
    }
  },

  emits: ['update:modelValue', 'blur'],

  data() {
    return {
      editorSettings: this.overrideSettings || {
        height: 400,
        plugins:
          'code preview searchreplace autolink autosave directionality visualblocks visualchars fullscreen image link media template codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount charmap quickbars emoticons powerpaste',
        menubar: 'edit view insert format tools table tc',
        toolbar:
          'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | lineheight outdent indent |  numlist bullist checklist | forecolor backcolor formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview code print | insertfile image media template link anchor codesample',
        placeholder: this.placeholder,
        paste_as_text: true,
        style_formats: [
          {
            title: 'Remove margin',
            selector: 'p,ul,ol,li,h1,h2,h3,h4,h5',
            styles: { 'margin-top': '0', 'margin-bottom': '0' },
          },
        ],
        style_formats_merge: true,
        font_size_formats: '6pt 7pt 8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 20pt 24pt 30pt',
        //font_family_formats: 'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats',
        extended_valid_elements: 'iframe[src]',
        ...this.appendSettings,
      },
      refreshKey: 0,
      updateThemeTimeout: null,
      skin: 'oxide',

      showTranslations: false,
      field: null
    };
  },

  created() {
    this.skin = this.mapSettings.darkTheme ? 'oxide-dark' : 'oxide';
  },

  methods: {
    init(editor) {
      if (this.autofocus) editor.focus();
      editor.on('blur', () => {
        this.sanitize();
        this.$emit('blur');
      });
    },

    sanitize() {
      if (!this.avoidSanitization) this.value = this.$purify.sanitize(this.value, { ALLOWED_ATTR: ['style', 'src', 'href'] });
    },

    openTranslations() {
      this.showTranslations = true;
      this.field = {
        label: this.label || '',
        value: this.value,
        type: 'EditorInput',
        translations: {}
      };
      if (this.translations) {
        for (let key in this.translations) {
          this.field.translations[key] = objectVariable(this.translations[key], this.translationsField);
        }
      } else {
        this.field.translations[this.$root.$i18n.locale] = this.value
      }
    },

    closeTranslations() {
      this.showTranslations = false;
      this.field = null;
    },

    onTranslationsSave(translations) {
      this.$emit('translate', translations, this.translationsField);
      this.closeTranslations();
    }
  },

  computed: {
    ...mapGetters({
      mapSettings: 'GET_MAP_SETTINGS',
    }),
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },

  watch: {
    'mapSettings.darkTheme'(val) {
      clearTimeout(this.updateThemeTimeout);
      this.updateThemeTimeout = setTimeout(() => {
        this.refreshKey++;
      }, 500);
      if (val) this.skin = 'oxide-dark';
      else this.skin = 'oxide';
    },
  },
};
</script>

<style lang="scss" scoped>
.invalid {
  .tox-tinymce {
    border-color: var(--error-color);
  }
}
.field-wrapper {
  position: relative;
  width: 100%;

  .field-icon-right {
    position: absolute;
    right: 10px;
    bottom: 30px;
    font-size: 1.4rem;

    a {
      color: var(--surface-900);
    }
  }
}
</style>
